import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import PledgeModel from 'donor-elf-web/models/pledge';
import AddSomethingModal, {
  type PledgeSavedArgs,
} from 'donor-elf-web/services/add-something-modal';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import Settings from 'donor-elf-web/services/settings';

export class PledgeRow {
  pledge: PledgeModel;
  @tracked selected: boolean = false;

  constructor(pledge: PledgeModel) {
    this.pledge = pledge;
  }
}

interface ComponentArgs {
  pledgeRows: PledgeRow[];
  sortBy?: (property: string) => void;
  showCheckboxes: boolean;
}

let NOOP = () => {};

export default class PledgesTable extends Component<ComponentArgs> {
  constructor(owner: Owner, args: ComponentArgs) {
    super(owner, args);
    this.savePledgeSubsciptionToken =
      this.addSomethingModal.subscribeToPledgeSaved(this.handlePledgeSaved);
  }

  @service declare addSomethingModal: AddSomethingModal;
  @service declare fundGoalService: FundGoalService;
  @service declare settings: Settings;

  @tracked deletedPledges: PledgeModel[] = [];
  @tracked selectAllChecked: boolean = false;

  savePledgeSubsciptionToken?: string;

  // removes deleted pledges
  get pledgeRows() {
    return this.args.pledgeRows.filter(
      (pr) => !this.deletedPledges.includes(pr.pledge),
    );
  }

  get showCheckboxes() {
    return this.args.showCheckboxes;
  }

  get sortBy() {
    return this.args.sortBy || NOOP;
  }

  get hasYtdOrTotalGoalType() {
    return (
      this.fundGoalService.hasYtdGoalType ||
      this.fundGoalService.hasTotalOrTotalAndExpectingGoalType
    );
  }

  get showAnnualAmount() {
    return (
      this.settings.userSettings?.showAnnualGoal ||
      this.fundGoalService.hasYtdGoalType
    );
  }

  handlePledgeSaved = async (_msg: string, data: PledgeSavedArgs) => {
    if (data.deleted) {
      this.deletedPledges = [...this.deletedPledges, data.pledge];
    }
  };

  toggleSelectAll = (event: InputEvent) => {
    const target = event.target as HTMLInputElement;
    this.selectAllChecked = target.checked;

    this.pledgeRows.forEach((pr) => {
      pr.selected = this.selectAllChecked;
    });
  };

  toggleSelect = (pledgeRow: PledgeRow) => {
    pledgeRow.selected = !pledgeRow.selected;
    this.selectAllChecked = this.pledgeRows.every((pi) => pi.selected);
  };

  willDestroy() {
    if (this.savePledgeSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.savePledgeSubsciptionToken,
      );
    }
  }
}
